"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Heatmap = void 0;
var tslib_1 = require("tslib");
var data_description_1 = require("./data/data-description");
var brush_1 = require("./extensions/brush");
var draw_1 = require("./extensions/draw");
var marker_1 = require("./extensions/marker");
var tooltip_1 = require("./extensions/tooltip");
var zoom_1 = require("./extensions/zoom");
var heatmap_core_1 = require("./heatmap-core");
/** Main class of the `heatmap-component` package.
 * Extends `HeatmapCore` by registering essential extensions and implementing useful public methods. */
var Heatmap = /** @class */function (_super) {
  tslib_1.__extends(Heatmap, _super);
  function Heatmap() {
    var _this = _super !== null && _super.apply(this, arguments) || this;
    /** Essential extension behaviors */
    _this.extensions = {};
    return _this;
  }
  Object.defineProperty(Heatmap.prototype, "events", {
    /** Custom events fired by the heatmap component, all are RxJS `BehaviorSubject` */
    get: function () {
      return this.state.events;
    },
    enumerable: false,
    configurable: true
  });
  /** Create a new `Heatmap` and optionaly set `data`.
   * If you omit the `data` parameter, you add data later via `.setData(data)`. */
  Heatmap.create = function (dataDescription) {
    if (dataDescription === void 0) {
      dataDescription = data_description_1.DataDescription.empty();
    }
    var heatmap = new this(dataDescription);
    heatmap.extensions.marker = heatmap.registerExtension(marker_1.MarkerExtension);
    heatmap.extensions.tooltip = heatmap.registerExtension(tooltip_1.TooltipExtension);
    heatmap.extensions.draw = heatmap.registerExtension(draw_1.DrawExtension);
    heatmap.extensions.zoom = heatmap.registerExtension(zoom_1.ZoomExtension);
    heatmap.extensions.brush = heatmap.registerExtension(brush_1.BrushExtension);
    return heatmap;
  };
  /** Replace current data by new data.
   * (If the new data are of different type, this method effectively changes the generic type parameters of `this`!
   * Returns re-typed `this`.) */
  Heatmap.prototype.setData = function (dataDescription) {
    this.state.setData(dataDescription);
    return this;
  };
  /** Change X and Y domain without changing the data (can be used for reordering or hiding columns/rows). */
  Heatmap.prototype.setDomains = function (xDomain, yDomain) {
    this.setData(tslib_1.__assign(tslib_1.__assign({}, this.state.originalData), {
      xDomain: xDomain !== null && xDomain !== void 0 ? xDomain : this.state.originalData.xDomain,
      yDomain: yDomain !== null && yDomain !== void 0 ? yDomain : this.state.originalData.yDomain
    }));
    return this;
  };
  /** Change the filter function without changing the underlying data (can be used for showing/hiding individual data cells). */
  Heatmap.prototype.setFilter = function (filter) {
    this.setData(tslib_1.__assign(tslib_1.__assign({}, this.state.originalData), {
      filter: filter
    }));
    return this;
  };
  /** Set a color provider function (takes a datum and cell position and returns color).
   * The returned color can be a CSS color (e.g. 'green', '#f00000', 'rgba(255,0,0,0.5)',
   * or `Color` type (uint32 color encoding).
   *
   * Example:
   * ```
   * heatmap.setColor((datum, x, y, xIndex, yIndex) => datum >= 0.5 ? 'black' : '#ff0000');
   * ```
   * Use `ColorScale` to create efficient color providers for big numeric data:
   * ```
   * heatmap.setColor(ColorScale.continuous('YlOrRd', [0, 100]));
   * ```
   */
  Heatmap.prototype.setColor = function (colorProvider) {
    var _a;
    (_a = this.extensions.draw) === null || _a === void 0 ? void 0 : _a.update({
      colorProvider: colorProvider
    });
    return this;
  };
  /** Set a tooltip provider function (takes a datum and cell position and returns tooltip HTML content).
   *
   * Example:
   * ```
   * heatmap.setTooltip((datum, x, y, xIndex, yIndex) => `<b>${datum}<\b>`);
   * ```
   * Call `heatmap.setTooltip(null)` to disable tooltips; `heatmap.setTooltip('default')` to reset the default tooltip provider.
   */
  Heatmap.prototype.setTooltip = function (tooltipProvider) {
    var _a;
    (_a = this.extensions.tooltip) === null || _a === void 0 ? void 0 : _a.update({
      tooltipProvider: tooltipProvider === 'default' ? tooltip_1.DefaultTooltipExtensionParams.tooltipProvider : tooltipProvider
    });
    return this;
  };
  /** Change visual parameters that cannot be changed via CSS
   * (gaps between drawn rectangles, marker corner radius). */
  Heatmap.prototype.setVisualParams = function (params) {
    var _a, _b;
    (_a = this.extensions.draw) === null || _a === void 0 ? void 0 : _a.update({
      xGapPixels: params.xGapPixels,
      xGapRelative: params.xGapRelative,
      yGapPixels: params.yGapPixels,
      yGapRelative: params.yGapRelative,
      minRectSizeForGaps: params.minRectSizeForGaps
    });
    (_b = this.extensions.marker) === null || _b === void 0 ? void 0 : _b.update({
      markerCornerRadius: params.markerCornerRadius
    });
    return this;
  };
  /** Set zooming parameters. Use `axis` parameter to turn zooming on/off.
   *
   * Example:
   * ```
   * heatmap.setZooming({ axis: "x" });    // Turn on zooming along the x-axis
   * heatmap.setZooming({ axis: "none" }); // Turn off zooming
   * ```
   */
  Heatmap.prototype.setZooming = function (params) {
    var _a;
    (_a = this.extensions.zoom) === null || _a === void 0 ? void 0 : _a.update(params);
    return this;
  };
  /** Controls how column/row indices and names are aligned to X and Y axes, when using `.zoom` and `.events.zoom` */
  Heatmap.prototype.setAlignment = function (x, y) {
    this.state.setAlignment(x, y);
    return this;
  };
  /** Enforce change of zoom and return the zoom value after the change */
  Heatmap.prototype.zoom = function (request) {
    return this.state.zoom(request);
  };
  /** Return current zoom */
  Heatmap.prototype.getZoom = function () {
    return this.state.getZoom();
  };
  /** Set brushing parameters. Use `enabled` parameter to turn brushing on/off.
   *
   * Example:
   * ```
   * heatmap.setBrushing({ enabled: true, snap: true, closeButton: true }); // Turn on brushing
   * heatmap.events.brush.subscribe(e => console.log('Brushed', e)); // Listen to brush selection changes
   * ```
   */
  Heatmap.prototype.setBrushing = function (params) {
    var _a;
    (_a = this.extensions.brush) === null || _a === void 0 ? void 0 : _a.update(params);
    return this;
  };
  return Heatmap;
}(heatmap_core_1.HeatmapCore);
exports.Heatmap = Heatmap;
// Possible TODOS for the future:
// TODO: Could: various zoom modes (horizontal, vertical, both, none...)
// TODO: Could: DataDescription.toArray2D - use Float32Array/Int32Array/... (+ mask array) when possible instead of Array (might speed up initialization up to 4x)
// TODO: Would: try setting `downsamplingPixelsPerRect` dynamically, based on rendering times
// TODO: Would: Smoothen zooming and panning with mouse wheel?
// TODO: Would: Tooltip/marker only showing on click?
// TODO: Would: add Behavior.onUnregister, use to keep a list of currently registered extensions?